import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth, MfaRequiredError } from "@/contexts/AuthContext";
import { Loader2, Eye, EyeOff, Shield, ArrowRight, ChevronLeft } from "lucide-react";
import { OAuthButtons, LastUsedBadge } from "@/components/auth/OAuthButtons";
import { consumePostAuthRedirect, isValidRedirectUrl, postAuthRedirectFromSearch } from "@/utils/oauth";
import { getLastLoginProvider, setLastLoginProvider } from "@/utils/lastLoginProvider";
import { OtpInput } from "@/components/ui/OtpInput";
import { AuthLayout, authInputClass, authLinkClass, authSubmitClass } from "@/components/auth/AuthLayout";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // 2FA state — null means no pending challenge
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const mfaInputRef = useRef<HTMLInputElement>(null);
  // Set when this tab submits its own login so onSubmit owns the (richer) redirect.
  const explicitLoginRef = useRef(false);
  // Read once on mount so the layout doesn't jump when a login in this tab updates the stored value
  const [lastUsedProvider] = useState(getLastLoginProvider);
  const emailLastUsed = lastUsedProvider === "email";

  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login, completeMfaChallenge, isAuthenticated, loading: authLoading } = useAuth();
  // logo.svg's wordmark is dark navy, invisible against the dark theme's background.

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // A team invite link passes the invited email, so the person signs in to the account the invite is for
      email: new URLSearchParams(location.search).get("email")?.trim() || "",
      password: "",
    },
  });

  // Store redirect URL if present
  useEffect(() => {
    const rd = postAuthRedirectFromSearch(location.search);
    if (rd) {
      localStorage.setItem("post_auth_redirect", rd);
    }
  }, [location.search]);

  // Redirect away if already authenticated — covers cross-tab login sync and direct visits
  // by a logged-in user. onSubmit still handles the explicit-login redirect with toast.
  useEffect(() => {
    if (authLoading || !isAuthenticated || explicitLoginRef.current) return;
    const storedRedirect = localStorage.getItem("post_auth_redirect");
    if (storedRedirect && storedRedirect.startsWith("/")) {
      localStorage.removeItem("post_auth_redirect");
      navigate(storedRedirect, { replace: true });
    } else {
      navigate("/~", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    explicitLoginRef.current = true;

    try {
      await login(values.email, values.password);
      setLastLoginProvider("email");

      toast({
        title: "Welcome back!",
        description: "Successfully logged in",
      });

      const savedRedirect = consumePostAuthRedirect();
      const urlRedirect = new URLSearchParams(window.location.search).get("rd");
      const storedRedirect = localStorage.getItem("post_auth_redirect");
      const redirectUrl = savedRedirect || urlRedirect || storedRedirect;

      // One shared validator: this inline copy accepted "//evil.com" as an in-app path (security scan F169).
      if (redirectUrl && isValidRedirectUrl(redirectUrl)) {
        localStorage.removeItem("post_auth_redirect");
        window.location.href = redirectUrl;
      } else {
        localStorage.removeItem("post_auth_redirect");
        navigate("/~", { replace: true });
      }
    } catch (error) {
      if (error instanceof MfaRequiredError) {
        // Keep explicitLoginRef set — the MFA submit owns the redirect on success.
        setMfaToken(error.mfaToken);
        setMfaCode("");
        setTimeout(() => mfaInputRef.current?.focus(), 50);
        return;
      }
      // Login failed: release ownership so cross-tab login sync can still redirect.
      explicitLoginRef.current = false;
      const err = error as any;
      if (err.status === 403 && err.message?.includes("deactivated")) {
        toast({
          variant: "destructive",
          title: "Account Deactivated",
          description: err.message,
        });
      } else if (err.status === 403 || err.message?.includes("not verified")) {
        navigate("/check-email", {
          state: { email: values.email }
        });
        toast({
          title: "Email Not Verified",
          description: "Please check your email to verify your account.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Login Failed",
          description: error instanceof Error ? error.message : "Invalid credentials. Please try again.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || !mfaCode.trim()) return;
    setIsLoading(true);
    try {
      await completeMfaChallenge(mfaToken, mfaCode.trim());
      setLastLoginProvider("email");
      toast({ title: "Welcome back!", description: "Successfully logged in" });
      const savedRedirect = consumePostAuthRedirect();
      const storedRedirect = localStorage.getItem("post_auth_redirect");
      const redirectUrl = savedRedirect || storedRedirect;
      if (redirectUrl) localStorage.removeItem("post_auth_redirect");
      if (redirectUrl && redirectUrl.startsWith("/")) {
        navigate(redirectUrl, { replace: true });
      } else {
        navigate("/~", { replace: true });
      }
    } catch (error) {
      // MFA failed: release ownership so cross-tab login sync can still redirect.
      explicitLoginRef.current = false;
      toast({
        variant: "destructive",
        title: "Verification Failed",
        description: error instanceof Error ? error.message : "Invalid code. Please try again.",
      });
      setMfaCode("");
      mfaInputRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSSOClick = () => {
    toast({
      title: "Coming Soon",
      description: "SSO authentication will be available in a future update.",
    });
  };

  const oauthSection = (
    <>
      <OAuthButtons lastUsedProvider={lastUsedProvider} />

      <button
        type="button"
        onClick={handleSSOClick}
        className="mx-auto mt-4 flex items-center gap-2 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
      >
        <Shield className="h-3.5 w-3.5" />
        Enterprise SSO
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </>
  );

  const emailForm = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[13px] font-semibold text-foreground">Email address</FormLabel>
              <FormControl>
                <Input placeholder="your@email.com" {...field} className={authInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[13px] font-semibold text-foreground">Password</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    {...field}
                    className={`${authInputClass} pr-11`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-0.5 top-0.5 h-10 w-10 hover:bg-soft"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end text-[13px]">
          <Link to="/forgot-password" className={authLinkClass}>
            Forgot your password?
          </Link>
        </div>

        <button type="submit" className={authSubmitClass} disabled={isLoading}>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {isLoading ? "Signing in..." : "Sign in"}
          {!isLoading && <ArrowRight className="h-4 w-4" />}
        </button>
      </form>
    </Form>
  );

  const divider = (label: string) => (
    <div className="my-6 flex items-center gap-4 text-[12px] text-muted-foreground">
      <span className="h-px flex-1 bg-line" />
      {label}
      <span className="h-px flex-1 bg-line" />
    </div>
  );

  return (
    <AuthLayout
      title={mfaToken ? "Two-factor authentication" : "Welcome back."}
      description={
        mfaToken
          ? useRecoveryCode
            ? "Enter one of your recovery codes"
            : "Enter the 6-digit code from your authenticator app"
          : "Your next idea is waiting. Pick up where you left off."
      }
    >
      {mfaToken ? (
        /* ── 2FA step ─────────────────────────────────────────────── */
        <>
          <form onSubmit={handleMfaSubmit} className="space-y-5">
            <div>
              {useRecoveryCode ? (
                <>
                  <label className="mb-2 block text-[13px] font-semibold text-foreground">
                    Recovery code
                  </label>
                  <Input
                    ref={mfaInputRef}
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value)}
                    placeholder="XXXXXX-XXXXXX-XXXXXX"
                    maxLength={26}
                    autoComplete="off"
                    className={`${authInputClass} text-center tracking-widest`}
                  />
                </>
              ) : (
                <OtpInput value={mfaCode} onChange={setMfaCode} autoFocus />
              )}
            </div>

            <button
              type="submit"
              className={authSubmitClass}
              disabled={isLoading || (useRecoveryCode ? !mfaCode.trim() : mfaCode.length < 6)}
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isLoading ? "Verifying..." : "Verify"}
            </button>
          </form>

          <div className="mt-5 flex flex-col items-center gap-3 text-[13px]">
            <button
              type="button"
              className={authLinkClass}
              onClick={() => { setUseRecoveryCode(r => !r); setMfaCode(""); }}
            >
              {useRecoveryCode ? "Use authenticator app instead" : "Use a recovery code instead"}
            </button>
            <button
              type="button"
              className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => { setMfaToken(null); setMfaCode(""); setUseRecoveryCode(false); }}
            >
              <ChevronLeft className="h-4 w-4" />
              Back to login
            </button>
          </div>
        </>
      ) : (
        /* ── Normal email/password step ───────────────────────────── */
        <>
          {emailLastUsed ? (
            <>
              <div data-testid="email-login-section" className="relative rounded-[12px] border border-plum p-4 ring-1 ring-plum/40">
                <LastUsedBadge />
                {emailForm}
              </div>
              {divider("or continue with")}
              {oauthSection}
            </>
          ) : (
            <>
              {oauthSection}
              {divider("or continue with email")}
              {emailForm}
            </>
          )}

          <p className="mt-6 text-center text-[13px] text-muted-foreground">
            New to Joylo?{" "}
            <Link to={{ pathname: "/signup", search: location.search }} className={authLinkClass}>
              Create an account
            </Link>
          </p>
        </>
      )}
    </AuthLayout>
  );
};

export default Login;